import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Save, Lock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SaveResultModal = ({ isOpen, onClose, evaluationData, onSaveSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setSaved(false);
      setError('');
    }
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  const handleSave = async () => {
    if (!evaluationData) {
      setError('No evaluation data to save');
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      console.log('Saving evaluation data:', evaluationData);
      
      // Get token from localStorage
      const token = localStorage.getItem('if_auth_token');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/evaluations/save`,
        evaluationData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      console.log('Save response:', response.data);

      if (response.data.success) {
        setSaved(true);
        
        setTimeout(() => {
          if (onSaveSuccess) {
            onSaveSuccess(response.data);
          }
          onClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to save evaluation');
      }
      
    } catch (err) {
      console.error('Save error:', err);
      
      if (err.response?.status === 401) {
        setError('Please login to save your evaluation');
        setTimeout(() => {
          localStorage.setItem('redirect_after_auth', window.location.pathname);
          navigate('/login');
        }, 2000);
      } else {
        const errorDetail = err.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
                            Array.isArray(errorDetail) ? errorDetail[0]?.msg || 'Validation error' :
                            err.message || 'Failed to save evaluation. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md relative animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100"
        >
          <X className="w-5 h-5" />
        </button>

        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            {saved ? (
              <>
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                Evaluation Saved!
              </>
            ) : (
              <>
                <Save className="w-6 h-6 text-purple-600" />
                Save Your Evaluation?
              </>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {saved ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <p className="text-slate-600">
                Your evaluation has been saved to your account!
              </p>
              <p className="text-sm text-slate-500 mt-2">
                You can now download your PDF certificate.
              </p>
            </div>
          ) : (
            <>
              <p className="text-slate-600">
                Would you like to save this evaluation result to your account? You can:
              </p>

              <ul className="space-y-2 text-sm text-slate-700 bg-blue-50 p-4 rounded-lg border border-blue-200">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Access your evaluation history anytime</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Download PDF certificates with unique verification ID</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Track improvements if you transform with OES</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>Compare scores across multiple papers</span>
                </li>
              </ul>

              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
                <Lock className="w-4 h-4 flex-shrink-0" />
                <span>An account is required to save evaluation results</span>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="flex-1"
                  disabled={saving}
                >
                  Skip for Now
                </Button>
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Result
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Use createPortal to render modal at document.body level
  return createPortal(modalContent, document.body);
};

export default SaveResultModal;
